import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import apiService, { Order, OrderDetails } from '../../services/api';

const AccountingWrapper = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const Header = styled.div`
  padding: var(--spacing-6);
  border-bottom: 1px solid var(--color-border);

  h2 {
    margin: 0 0 var(--spacing-2) 0;
    color: var(--color-text);
    font-size: var(--font-size-xl);
    font-weight: 600;
  }

  p {
    margin: 0;
    color: var(--color-text-light);
    font-size: var(--font-size-sm);
  }
`;

const Content = styled.div`
  padding: var(--spacing-6);
  display: grid;
  gap: var(--spacing-6);
`
;

const Panel = styled.div`
  background: #f9fafb;
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-5);
  border: 1px solid #e5e7eb;
`;

const PanelTitle = styled.h3`
  margin: 0 0 var(--spacing-3) 0;
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: var(--color-secondary);
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--spacing-4);
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  font-size: var(--font-size-sm);
  color: var(--color-text-light);
  gap: var(--spacing-2);
`;

const Input = styled.input`
  padding: var(--spacing-3);
  border-radius: var(--border-radius);
  border: 1px solid var(--color-border);
  font-size: var(--font-size-base);
  background: white;
`;

const TextArea = styled.textarea`
  padding: var(--spacing-3);
  border-radius: var(--border-radius);
  border: 1px solid var(--color-border);
  font-size: var(--font-size-base);
  min-height: 80px;
  background: white;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-3);
  margin-top: var(--spacing-4);
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: var(--spacing-3) var(--spacing-4);
  border-radius: var(--border-radius);
  border: none;
  cursor: pointer;
  font-weight: 600;
  background: ${({ variant }) => variant === 'secondary' ? '#E5E7EB' : 'var(--color-secondary)'};
  color: ${({ variant }) => variant === 'secondary' ? 'var(--color-text)' : 'white'};
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-2);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-2);
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-3);
`;

const Select = styled.select`
  padding: var(--spacing-3);
  border-radius: var(--border-radius);
  border: 1px solid var(--color-border);
  background: white;
  font-size: var(--font-size-base);
`;

const OrdersTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: var(--spacing-3);
    border-bottom: 1px solid var(--color-border);
    text-align: left;
    font-size: var(--font-size-sm);
  }

  th {
    background: #f1f5f9;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  tbody tr:hover {
    background: #f9fafb;
  }
`;

const EmptyState = styled.div`
  padding: var(--spacing-6);
  text-align: center;
  color: var(--color-text-light);
  font-style: italic;
`;

const Badge = styled.span<{ tone?: 'success' | 'warning' | 'info' }>`
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-block;
  background: ${({ tone }) => {
    switch (tone) {
      case 'success':
        return '#DCFCE7';
      case 'warning':
        return '#FEF9C3';
      case 'info':
      default:
        return '#E0F2FE';
    }
  }};
  color: ${({ tone }) => {
    switch (tone) {
      case 'success':
        return '#15803D';
      case 'warning':
        return '#B45309';
      case 'info':
      default:
        return '#0369A1';
    }
  }};
`;

const TipsList = styled.ul`
  margin: 0;
  padding-left: 1.5rem;
  color: var(--color-text-light);
  font-size: var(--font-size-sm);

  li + li {
    margin-top: var(--spacing-2);
  }
`;

const Loader = styled.div`
  padding: var(--spacing-6);
  text-align: center;
`;

const ErrorText = styled.div`
  padding: var(--spacing-4);
  border-radius: var(--border-radius);
  background: #fee2e2;
  color: #dc2626;
  font-weight: 500;
`;

const HelperText = styled.p`
  margin: var(--spacing-2) 0 0 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-light);
  grid-column: 1 / -1;
`;

const TabRow = styled.div`
  display: flex;
  gap: var(--spacing-3);
  padding: var(--spacing-4) var(--spacing-6) 0 var(--spacing-6);
  border-bottom: 1px solid var(--color-border);
  background: #f8fafc;
`;

const TabButton = styled.button<{ $active?: boolean }>`
  background: ${({ $active }) => ($active ? 'white' : 'transparent')};
  border: ${({ $active }) => ($active ? '1px solid var(--color-border)' : '1px solid transparent')};
  border-bottom: ${({ $active }) => ($active ? '1px solid white' : '1px solid transparent')};
  border-radius: var(--border-radius) var(--border-radius) 0 0;
  padding: var(--spacing-3) var(--spacing-4);
  cursor: pointer;
  font-weight: 600;
  color: ${({ $active }) => ($active ? 'var(--color-secondary)' : 'var(--color-text-light)')};
  transition: all 0.2s ease;

  &:hover {
    color: var(--color-secondary);
    background: white;
  }
`;

const CreditSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--spacing-4);
`;

const SummaryCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: var(--border-radius);
  padding: var(--spacing-3) var(--spacing-4);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);

  .label {
    font-size: var(--font-size-xs);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--color-text-light);
  }

  .value {
    font-size: var(--font-size-base);
    font-weight: 600;
    color: var(--color-text);
    word-break: break-word;
  }
`;

const HistoryTable = styled(OrdersTable)`
  margin-top: var(--spacing-4);

  tbody tr td:last-child {
    width: 1%;
    white-space: nowrap;
  }
`;

interface CompanyProfile {
  companyName: string;
  orgNumber: string;
  vatNumber: string;
  addressLine: string;
  postalCode: string;
  city: string;
  phone: string;
  email: string;
  website: string;
  bankgiro: string;
  extraNotes: string;
}

const PROFILE_STORAGE_KEY = 'lillaflora_accounting_profile_v1';

const defaultProfile: CompanyProfile = {
  companyName: 'Lillaflora',
  orgNumber: '',
  vatNumber: '',
  addressLine: '',
  postalCode: '',
  city: '',
  phone: '',
  email: 'info@lillaflora.nu',
  website: 'https://lillaflora.nu',
  bankgiro: '',
  extraNotes: ''
};

const formatCurrency = (value: number) => new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(value);
const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit' });

interface CreditReceiptRecord {
  id: string;
  orderId: number;
  orderNumber: string;
  creditNumber: string;
  amount: number;
  reason: string;
  issuedAt: string;
  createdAt: string;
  notes?: string;
}

const CREDIT_HISTORY_STORAGE_KEY = 'lillaflora_credit_history_v1';

const AdminAccounting: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'receipts' | 'credits'>('receipts');
  const [profile, setProfile] = useState<CompanyProfile>(defaultProfile);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const [selectedYear, setSelectedYear] = useState<string>('alla');
  const [selectedMonth, setSelectedMonth] = useState<string>('alla');
  const [paymentFilter, setPaymentFilter] = useState<'alla' | 'paid' | 'pending' | 'failed' | 'refunded'>('alla');
  const [statusFilter, setStatusFilter] = useState<'alla' | 'pending' | 'sent' | 'cancelled'>('alla');
  const [selectedCreditOrderId, setSelectedCreditOrderId] = useState<number | ''>('');
  const [creditAmount, setCreditAmount] = useState<string>('');
  const [creditDate, setCreditDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [creditReason, setCreditReason] = useState<string>('Återbetalning till kund');
  const [creditNotes, setCreditNotes] = useState<string>('');
  const [creditNumber, setCreditNumber] = useState<string>('');
  const [creditGenerating, setCreditGenerating] = useState<boolean>(false);
  const [creditHistory, setCreditHistory] = useState<CreditReceiptRecord[]>([]);
  const [sendingId, setSendingId] = useState<number | null>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(PROFILE_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setProfile({ ...defaultProfile, ...parsed });
      }
      const storedHistory = localStorage.getItem(CREDIT_HISTORY_STORAGE_KEY);
      if (storedHistory) {
        const parsedHistory = JSON.parse(storedHistory) as CreditReceiptRecord[];
        setCreditHistory(parsedHistory);
      }
    } catch (err) {
      console.error('Failed to load accounting profile', err);
    }
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError('');
      try {
        const result = await apiService.getOrders({ limit: 500, sort_by: 'created_at', sort_order: 'DESC' });
        setOrders(result || []);
      } catch (err: any) {
        setError(err?.message || 'Kunde inte hämta orders');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const years = useMemo(() => {
    const unique = new Set<string>();
    orders.forEach(order => {
      unique.add(new Date(order.created_at).getFullYear().toString());
    });
    return Array.from(unique).sort((a, b) => parseInt(b) - parseInt(a));
  }, [orders]);

  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const date = new Date(order.created_at);
      const year = date.getFullYear().toString();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');

      if (selectedYear !== 'alla' && year !== selectedYear) return false;
      if (selectedMonth !== 'alla' && month !== selectedMonth) return false;
      if (paymentFilter !== 'alla' && order.payment_status !== paymentFilter) return false;
      if (statusFilter !== 'alla' && order.status !== statusFilter) return false;
      return true;
    });
  }, [orders, selectedYear, selectedMonth, paymentFilter, statusFilter]);

  const totals = useMemo(() => {
    const base = filteredOrders.reduce((acc, order) => {
      return {
        total: acc.total + order.total_amount,
        tax: acc.tax + order.tax_amount,
        shipping: acc.shipping + order.shipping_cost
      };
    }, { total: 0, tax: 0, shipping: 0 });

    return {
      ...base,
      net: base.total - base.tax
    };
  }, [filteredOrders]);

  const refundedOrders = useMemo(() => {
    return orders.filter(order => order.payment_status === 'refunded' || order.status === 'cancelled');
  }, [orders]);

  const creditEligibleOrders = useMemo(() => {
    if (refundedOrders.length > 0) {
      return refundedOrders;
    }
    return orders;
  }, [orders, refundedOrders]);

  const selectedCreditOrder = useMemo(() => {
    if (typeof selectedCreditOrderId !== 'number') {
      return null;
    }
    return orders.find(order => order.id === selectedCreditOrderId) || null;
  }, [orders, selectedCreditOrderId]);

  useEffect(() => {
    if (typeof selectedCreditOrderId !== 'number') {
      setCreditAmount('');
      setCreditNumber('');
      return;
    }
    const order = orders.find(o => o.id === selectedCreditOrderId);
    if (order) {
      setCreditAmount(order.total_amount.toFixed(2));
      const suffix = new Date().toISOString().slice(2, 10).replace(/-/g, '');
      setCreditNumber(`${order.order_number}-CR-${suffix}`);
    }
  }, [orders, selectedCreditOrderId]);

  const handleProfileChange = (field: keyof CompanyProfile, value: string) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = () => {
    localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profile));
  };

  const handleResetProfile = () => {
    setProfile(defaultProfile);
    localStorage.removeItem(PROFILE_STORAGE_KEY);
  };

  const buildReceiptHtml = (order: OrderDetails) => {
    const issueDate = formatDate(order.created_at);
    const netAmount = order.total_amount - order.tax_amount;
    const shippingRow = order.shipping_cost > 0
      ? `<tr><td colspan="2">Frakt</td><td style="text-align:right">${formatCurrency(order.shipping_cost)}</td></tr>`
      : '';

    const itemsRows = order.items.map(item => `
      <tr>
        <td>${item.product_name}</td>
        <td>${item.quantity} st á ${formatCurrency(item.price)}</td>
        <td style="text-align:right">${formatCurrency(item.total_price || item.price * item.quantity)}</td>
      </tr>
    `).join('');

    const campaignRow = order.campaign_discount_amount && order.campaign_discount_amount > 0 ? `
      <tr>
        <td colspan="2">Rabatt (${order.campaign_code || ''})</td>
        <td style="text-align:right">-${formatCurrency(order.campaign_discount_amount)}</td>
      </tr>
    ` : '';

    const companyAddress = [profile.addressLine, `${profile.postalCode} ${profile.city}`.trim()].filter(Boolean).join(', ');

    return `<!doctype html>
<html lang="sv">
<head>
  <meta charset="utf-8" />
  <title>Kvitto ${order.order_number}</title>
  <style>
    body { font-family: 'Helvetica Neue', Arial, sans-serif; margin: 40px; color: #111827; }
    h1 { margin-bottom: 4px; font-size: 26px; color: #0f172a; }
    h2 { margin-top: 32px; font-size: 18px; color: #1f2937; }
    .company, .customer { margin-bottom: 24px; }
    .meta-table { width: 100%; border-collapse: collapse; margin-top: 16px; }
    .meta-table td { padding: 4px 0; }
    .meta-table td:first-child { width: 180px; font-weight: 600; }
    table.items { width: 100%; border-collapse: collapse; margin-top: 24px; }
    table.items th { text-align: left; border-bottom: 2px solid #1f2937; padding: 8px 0; }
    table.items td { padding: 8px 0; border-bottom: 1px solid #e5e7eb; }
    .totals { margin-top: 24px; width: 100%; }
    .totals td { padding: 6px 0; }
    .totals td:first-child { font-weight: 600; }
    .footer { margin-top: 36px; font-size: 13px; color: #4b5563; }
    .legal { margin-top: 16px; font-size: 12px; color: #6b7280; }
  </style>
</head>
<body>
  <h1>Kvitto</h1>
  <div class="company">
    <strong>${profile.companyName}</strong><br/>
    ${companyAddress || ''}<br/>
    ${profile.orgNumber ? `Org.nr: ${profile.orgNumber}<br/>` : ''}
    ${profile.vatNumber ? `Momsregistreringsnummer: ${profile.vatNumber}<br/>` : ''}
    ${profile.phone ? `Telefon: ${profile.phone}<br/>` : ''}
    ${profile.email ? `E-post: ${profile.email}<br/>` : ''}
    ${profile.bankgiro ? `Bankgiro: ${profile.bankgiro}<br/>` : ''}
    ${profile.website || ''}
  </div>

  <table class="meta-table">
    <tr><td>Ordernummer</td><td>${order.order_number}</td></tr>
    <tr><td>Kvittonummer</td><td>${order.order_number}</td></tr>
    <tr><td>Orderdatum</td><td>${issueDate}</td></tr>
    <tr><td>Betalningsmetod</td><td>${order.payment_method || 'Kort/Klarna'}</td></tr>
    <tr><td>Betalningsstatus</td><td>${order.payment_status}</td></tr>
  </table>

  <h2>Kunduppgifter</h2>
  <div class="customer">
    ${order.first_name} ${order.last_name}<br/>
    ${order.email}<br/>
    ${order.phone || ''}<br/>
    ${order.billing_address || order.shipping_address || ''}
  </div>

  <h2>Orderrader</h2>
  <table class="items">
    <thead>
      <tr>
        <th>Artikel</th>
        <th>Antal & à-pris</th>
        <th style="text-align:right">Summa</th>
      </tr>
    </thead>
    <tbody>
      ${itemsRows}
      ${shippingRow}
      ${campaignRow}
    </tbody>
  </table>

  <table class="totals">
    <tr><td>Belopp exkl. moms</td><td style="text-align:right">${formatCurrency(netAmount)}</td></tr>
    <tr><td>Moms (25%)</td><td style="text-align:right">${formatCurrency(order.tax_amount)}</td></tr>
    <tr><td><strong>Totalt att betala</strong></td><td style="text-align:right"><strong>${formatCurrency(order.total_amount)}</strong></td></tr>
  </table>

  <div class="legal">
    Kvitto genererat ${new Date().toLocaleString('sv-SE')}. Spara kvittot i minst sju år enligt bokföringslagen.
  </div>
  ${profile.extraNotes ? `<div class="legal">${profile.extraNotes}</div>` : ''}

  <script>
    window.addEventListener('load', () => {
      setTimeout(() => {
        window.print();
      }, 400);
    });
  </script>
</body>
</html>`;
  };

  const buildCreditReceiptHtml = (
    order: OrderDetails,
    amount: number,
    params: { creditDate: string; reason: string; notes: string; creditNumber: string }
  ) => {
    const normalizedDate = params.creditDate || new Date().toISOString().slice(0, 10);
    const creditIssuedDate = new Date(normalizedDate).toLocaleDateString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit' });
    const orderDate = formatDate(order.created_at);
    const safeAmount = Math.abs(amount);
    const ratio = order.total_amount > 0 ? order.tax_amount / order.total_amount : 0.2;
    const refundTax = parseFloat((safeAmount * ratio).toFixed(2));
    const refundNet = parseFloat((safeAmount - refundTax).toFixed(2));
    const companyAddress = [profile.addressLine, `${profile.postalCode} ${profile.city}`.trim()].filter(Boolean).join(', ');

    return `<!doctype html>
<html lang="sv">
<head>
  <meta charset="utf-8" />
  <title>Kreditering ${params.creditNumber}</title>
  <style>
    body { font-family: 'Helvetica Neue', Arial, sans-serif; margin: 40px; color: #111827; }
    h1 { margin-bottom: 4px; font-size: 26px; color: #0f172a; }
    h2 { margin-top: 32px; font-size: 18px; color: #1f2937; }
    .company, .customer { margin-bottom: 24px; }
    .meta-table { width: 100%; border-collapse: collapse; margin-top: 16px; }
    .meta-table td { padding: 4px 0; }
    .meta-table td:first-child { width: 220px; font-weight: 600; }
    table.items { width: 100%; border-collapse: collapse; margin-top: 24px; }
    table.items th { text-align: left; border-bottom: 2px solid #1f2937; padding: 8px 0; }
    table.items td { padding: 10px 0; border-bottom: 1px solid #e5e7eb; }
    .totals { margin-top: 24px; width: 100%; }
    .totals td { padding: 6px 0; }
    .totals td:first-child { font-weight: 600; }
    .footer { margin-top: 36px; font-size: 13px; color: #4b5563; }
    .legal { margin-top: 16px; font-size: 12px; color: #6b7280; }
  </style>
</head>
<body>
  <h1>Krediteringskvitto</h1>
  <div class="company">
    <strong>${profile.companyName}</strong><br/>
    ${companyAddress || ''}<br/>
    ${profile.orgNumber ? `Org.nr: ${profile.orgNumber}<br/>` : ''}
    ${profile.vatNumber ? `Momsregistreringsnummer: ${profile.vatNumber}<br/>` : ''}
    ${profile.phone ? `Telefon: ${profile.phone}<br/>` : ''}
    ${profile.email ? `E-post: ${profile.email}<br/>` : ''}
    ${profile.bankgiro ? `Bankgiro: ${profile.bankgiro}<br/>` : ''}
    ${profile.website || ''}
  </div>

  <table class="meta-table">
    <tr><td>Kreditnummer</td><td>${params.creditNumber}</td></tr>
    <tr><td>Datum för kreditering</td><td>${creditIssuedDate}</td></tr>
    <tr><td>Relaterat ordernummer</td><td>${order.order_number}</td></tr>
    <tr><td>Orderdatum</td><td>${orderDate}</td></tr>
    <tr><td>Betalningsmetod</td><td>${order.payment_method || 'Kort/Klarna'}</td></tr>
    <tr><td>Återbetalningsorsak</td><td>${params.reason}</td></tr>
  </table>

  <h2>Kunduppgifter</h2>
  <div class="customer">
    ${order.first_name} ${order.last_name}<br/>
    ${order.email}<br/>
    ${order.phone || ''}<br/>
    ${order.billing_address || order.shipping_address || ''}
  </div>

  <h2>Kreditering</h2>
  <table class="items">
    <thead>
      <tr>
        <th>Rad</th>
        <th>Beskrivning</th>
        <th style="text-align:right">Belopp</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>1</td>
        <td>${params.reason}</td>
        <td style="text-align:right">${formatCurrency(-safeAmount)}</td>
      </tr>
    </tbody>
  </table>

  <table class="totals">
    <tr><td>Belopp exkl. moms</td><td style="text-align:right">${formatCurrency(-refundNet)}</td></tr>
    <tr><td>Moms (återförd)</td><td style="text-align:right">${formatCurrency(-refundTax)}</td></tr>
    <tr><td><strong>Totalt krediterat</strong></td><td style="text-align:right"><strong>${formatCurrency(-safeAmount)}</strong></td></tr>
  </table>

  <div class="legal">
    Krediteringen återför ${formatCurrency(safeAmount)} till kundens betalning. Spara kreditnotan tillsammans med tillhörande orderkvitto enligt bokföringslagen.
  </div>
  ${params.notes ? `<div class="legal">${params.notes}</div>` : ''}

  <script>
    window.addEventListener('load', () => {
      setTimeout(() => {
        window.print();
      }, 400);
    });
  </script>
</body>
</html>`;
  };

  const handleDownloadReceipt = async (order: Order) => {
    setDownloadingId(order.id);
    try {
      const details = await apiService.getOrderDetails(order.id);
      const receiptHtml = buildReceiptHtml(details);
      const receiptWindow = window.open('', '_blank');
      if (!receiptWindow) {
        alert('Popup-blocker förhindrade kvittot. Tillåt popup-fönster och försök igen.');
        return;
      }

      receiptWindow.document.open();
      receiptWindow.document.write(receiptHtml);
      receiptWindow.document.close();
    } catch (err: any) {
      alert(err?.message || 'Kunde inte generera kvittot.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleSendForScanning = async (order: Order) => {
    if (sendingId !== null) {
      return;
    }
    setSendingId(order.id);
    try {
      await apiService.sendOrderToScanning(order.id);
      alert(`Kvitto för order ${order.order_number} skickades till inskanning.`);
    } catch (err: any) {
      alert(err?.message || 'Kunde inte skicka kvittot till inskanning.');
    } finally {
      setSendingId(null);
    }
  };

  const handleGenerateCreditReceipt = async () => {
    if (typeof selectedCreditOrderId !== 'number') {
      alert('Välj en order att kreditera.');
      return;
    }
    const normalizedAmount = parseFloat(creditAmount.replace(',', '.'));
    if (Number.isNaN(normalizedAmount) || normalizedAmount <= 0) {
      alert('Ange ett återbetalat belopp större än 0.');
      return;
    }
    if (!creditNumber.trim()) {
      alert('Ange ett kreditnummer.');
      return;
    }

    setCreditGenerating(true);
    try {
      const orderDetails = await apiService.getOrderDetails(selectedCreditOrderId);
      const receiptHtml = buildCreditReceiptHtml(orderDetails, normalizedAmount, {
        creditDate,
        reason: creditReason,
        notes: creditNotes,
        creditNumber: creditNumber.trim()
      });
      const receiptWindow = window.open('', '_blank');
      if (!receiptWindow) {
        alert('Popup-blocker förhindrade kreditnotan. Tillåt popup-fönster och försök igen.');
        return;
      }

      receiptWindow.document.open();
      receiptWindow.document.write(receiptHtml);
      receiptWindow.document.close();

      const historyEntry: CreditReceiptRecord = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        orderId: orderDetails.id,
        orderNumber: orderDetails.order_number,
        creditNumber: creditNumber.trim(),
        amount: -Math.abs(normalizedAmount),
        reason: creditReason,
        issuedAt: creditDate,
        createdAt: new Date().toISOString(),
        notes: creditNotes?.trim() || undefined
      };
      setCreditHistory(prev => {
        const updated = [historyEntry, ...prev];
        localStorage.setItem(CREDIT_HISTORY_STORAGE_KEY, JSON.stringify(updated));
        return updated;
      });
    } catch (err: any) {
      alert(err?.message || 'Kunde inte generera kreditnotan.');
    } finally {
      setCreditGenerating(false);
    }
  };

  const handleRemoveCreditHistory = (id: string) => {
    setCreditHistory(prev => {
      const updated = prev.filter(entry => entry.id !== id);
      localStorage.setItem(CREDIT_HISTORY_STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  const handleClearCreditForm = () => {
    setSelectedCreditOrderId('');
    setCreditAmount('');
    setCreditReason('Återbetalning till kund');
    setCreditNotes('');
    setCreditNumber('');
  };

  const handleDownloadCsv = () => {
    if (!filteredOrders.length) {
      alert('Det finns inga ordrar för vald period.');
      return;
    }

    const header = ['Ordernummer', 'Datum', 'Kund', 'E-post', 'Totalt', 'Moms', 'Betalstatus', 'Leveransstatus'];
    const rows = filteredOrders.map(order => [
      order.order_number,
      formatDate(order.created_at),
      `${order.first_name} ${order.last_name}`.trim(),
      order.email,
      order.total_amount.toFixed(2).replace('.', ','),
      order.tax_amount.toFixed(2).replace('.', ','),
      order.payment_status,
      order.status
    ]);

    const csv = [header, ...rows].map(row => row.map(cell => `"${cell}"`).join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const descriptor = selectedYear === 'alla' ? 'alla-ar' : `${selectedYear}-${selectedMonth !== 'alla' ? selectedMonth : 'alla-manader'}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = `bokforing-${descriptor}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <AccountingWrapper>
      <Header>
        <h2>Bokföring & kvitton</h2>
        <p>Exportera lagstadgade kvitton och skapa bokföringsunderlag för Skatteverket.</p>
      </Header>

      <TabRow>
        <TabButton $active={activeTab === 'receipts'} onClick={() => setActiveTab('receipts')}>
          Kvitton
        </TabButton>
        <TabButton $active={activeTab === 'credits'} onClick={() => setActiveTab('credits')}>
          Krediteringar
        </TabButton>
      </TabRow>

      {activeTab === 'receipts' && (
      <Content>
        <Panel>
          <PanelTitle>Företagsuppgifter för kvitton</PanelTitle>
          <FormGrid>
            <Label>
              Företagsnamn
              <Input value={profile.companyName} onChange={e => handleProfileChange('companyName', e.target.value)} />
            </Label>
            <Label>
              Organisationsnummer
              <Input value={profile.orgNumber} onChange={e => handleProfileChange('orgNumber', e.target.value)} />
            </Label>
            <Label>
              Momsregistreringsnummer
              <Input value={profile.vatNumber} onChange={e => handleProfileChange('vatNumber', e.target.value)} />
            </Label>
            <Label>
              Besöks-/Fakturaadress
              <Input value={profile.addressLine} onChange={e => handleProfileChange('addressLine', e.target.value)} placeholder="Storgatan 1" />
            </Label>
            <Label>
              Postnummer
              <Input value={profile.postalCode} onChange={e => handleProfileChange('postalCode', e.target.value)} placeholder="12345" />
            </Label>
            <Label>
              Stad
              <Input value={profile.city} onChange={e => handleProfileChange('city', e.target.value)} />
            </Label>
            <Label>
              Telefon
              <Input value={profile.phone} onChange={e => handleProfileChange('phone', e.target.value)} />
            </Label>
            <Label>
              E-postadress
              <Input value={profile.email} onChange={e => handleProfileChange('email', e.target.value)} />
            </Label>
            <Label>
              Hemsida
              <Input value={profile.website} onChange={e => handleProfileChange('website', e.target.value)} />
            </Label>
            <Label>
              Bankgiro / Swish
              <Input value={profile.bankgiro} onChange={e => handleProfileChange('bankgiro', e.target.value)} />
            </Label>
          </FormGrid>
          <Label style={{ marginTop: 'var(--spacing-4)' }}>
            Extra noteringar (visas längst ner på kvittot)
            <TextArea value={profile.extraNotes} onChange={e => handleProfileChange('extraNotes', e.target.value)} placeholder="Exempel: Tack för ditt köp!" />
          </Label>
          <ButtonRow>
            <Button onClick={handleSaveProfile}>💾 Spara uppgifter</Button>
            <Button variant="secondary" onClick={handleResetProfile}>↺ Återställ</Button>
          </ButtonRow>
        </Panel>

        <Panel>
          <PanelTitle>Filtrera ordrar</PanelTitle>
          <FilterRow>
            <Label>
              År
              <Select value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
                <option value="alla">Alla år</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </Select>
            </Label>
            <Label>
              Månad
              <Select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}>
                <option value="alla">Alla månader</option>
                {Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0')).map(month => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </Select>
            </Label>
            <Label>
              Betalstatus
              <Select value={paymentFilter} onChange={e => setPaymentFilter(e.target.value as typeof paymentFilter)}>
                <option value="alla">Alla</option>
                <option value="paid">Betald</option>
                <option value="pending">Pågående</option>
                <option value="failed">Misslyckad</option>
                <option value="refunded">Återbetald</option>
              </Select>
            </Label>
            <Label>
              Leveransstatus
              <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value as typeof statusFilter)}>
                <option value="alla">Alla</option>
                <option value="pending">Ej skickad</option>
                <option value="sent">Skickad</option>
                <option value="cancelled">Avbruten</option>
              </Select>
            </Label>
          </FilterRow>

          <ButtonRow>
            <Button onClick={handleDownloadCsv}>📥 Exportera CSV</Button>
            <Badge tone="info">{filteredOrders.length} ordrar i urvalet</Badge>
            <Badge tone="success">Omsättning: {formatCurrency(totals.total)}</Badge>
            <Badge tone="info">Moms: {formatCurrency(totals.tax)}</Badge>
          </ButtonRow>
        </Panel>

        <Panel>
          <PanelTitle>Kvitton</PanelTitle>
          {loading && <Loader>Laddar ordrar...</Loader>}
          {error && <ErrorText>{error}</ErrorText>}
          {!loading && !error && filteredOrders.length === 0 && (
            <EmptyState>Inga ordrar hittades för vald filtrering.</EmptyState>
          )}
          {!loading && !error && filteredOrders.length > 0 && (
            <OrdersTable>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Datum</th>
                  <th>Kund</th>
                  <th>Belopp</th>
                  <th>Moms</th>
                  <th>Status</th>
                  <th>Åtgärd</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map(order => (
                  <tr key={order.id}>
                    <td>{order.order_number}</td>
                    <td>{formatDate(order.created_at)}</td>
                    <td>{order.first_name} {order.last_name}</td>
                    <td>{formatCurrency(order.total_amount)}</td>
                    <td>{formatCurrency(order.tax_amount)}</td>
                    <td>
                      <Badge tone={order.payment_status === 'paid' ? 'success' : order.payment_status === 'pending' ? 'warning' : 'info'}>
                        {order.payment_status}
                      </Badge>
                    </td>
                    <td>
                      <ActionButtons>
                        <Button onClick={() => handleDownloadReceipt(order)} disabled={downloadingId === order.id || sendingId === order.id}>
                          {downloadingId === order.id ? 'Genererar…' : 'Ladda ner kvitto'}
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => handleSendForScanning(order)}
                          disabled={sendingId === order.id || downloadingId === order.id}
                        >
                          {sendingId === order.id ? 'Skickar…' : 'Skicka till inskanning'}
                        </Button>
                      </ActionButtons>
                    </td>
                  </tr>
                ))}
              </tbody>
            </OrdersTable>
          )}
        </Panel>

        <Panel>
          <PanelTitle>Tips för svensk bokföring</PanelTitle>
          <TipsList>
            <li>Spara alla kvitton försålda varor i minst 7 år (Bokföringslagen).</li>
            <li>Verifiera att momsinkomster matchar rapporteringen till Skatteverket.</li>
            <li>Inkludera organisationsnummer, kontaktuppgifter och betalningssätt på varje kvitto.</li>
            <li>Vid kontantförsäljning ska kvitton lämnas till kund utan dröjsmål.</li>
            <li>Exportera CSV-filen varje månad och arkivera tillsammans med kontoutdrag.</li>
          </TipsList>
        </Panel>
      </Content>
      )}

      {activeTab === 'credits' && (
      <Content>
        <Panel>
          <PanelTitle>Krediteringar & återbetalningar</PanelTitle>
          <p style={{ marginBottom: 'var(--spacing-4)', color: 'var(--color-text-light)' }}>
            Skapa krediteringskvitton för återbetalda order. Spara kreditnotan tillsammans med originalkvittot.
          </p>
          <CreditSummary>
            <SummaryCard>
              <span className="label">Återbetalda ordrar</span>
              <span className="value">{refundedOrders.length}</span>
            </SummaryCard>
            <SummaryCard>
              <span className="label">Senaste återbetalning</span>
              <span className="value">
                {refundedOrders[0] ? formatDate(refundedOrders[0].updated_at || refundedOrders[0].created_at) : 'Ingen registrerad'}
              </span>
            </SummaryCard>
            <SummaryCard>
              <span className="label">Genererade kreditnotor</span>
              <span className="value">{creditHistory.length}</span>
            </SummaryCard>
          </CreditSummary>
        </Panel>

        <Panel>
          <PanelTitle>Skapa kreditnota</PanelTitle>
          <FormGrid>
            <Label>
              Välj återbetald order
              <Select
                value={selectedCreditOrderId === '' ? '' : selectedCreditOrderId}
                onChange={e => setSelectedCreditOrderId(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Välj order…</option>
                {creditEligibleOrders.map(order => (
                  <option key={order.id} value={order.id}>
                    {order.order_number} · {order.first_name} {order.last_name} · {formatCurrency(order.total_amount)}
                  </option>
                ))}
              </Select>
            </Label>
            {refundedOrders.length === 0 && orders.length > 0 && (
              <HelperText>Inga återbetalda ordrar hittades, visar samtliga ordrar.</HelperText>
            )}
            <Label>
              Kreditnummer
              <Input value={creditNumber} onChange={e => setCreditNumber(e.target.value)} placeholder="ORDER-CR-ÅÅMMDD" />
            </Label>
            <Label>
              Datum för återbetalning
              <Input type="date" value={creditDate} onChange={e => setCreditDate(e.target.value)} />
            </Label>
            <Label>
              Återbetalat belopp (inkl. moms)
              <Input
                type="number"
                step="0.01"
                min="0"
                value={creditAmount}
                onChange={e => setCreditAmount(e.target.value)}
                placeholder="0,00"
              />
            </Label>
            <Label>
              Orsak till kreditering
              <Input value={creditReason} onChange={e => setCreditReason(e.target.value)} />
            </Label>
          </FormGrid>
          <Label style={{ marginTop: 'var(--spacing-4)' }}>
            Interna anteckningar / kommentar
            <TextArea
              value={creditNotes}
              onChange={e => setCreditNotes(e.target.value)}
              placeholder="Exempel: Retur av defekt produkt, återbetalad via Stripe."
            />
          </Label>

          {selectedCreditOrder && (
            <div style={{ marginTop: 'var(--spacing-4)' }}>
              <PanelTitle style={{ marginBottom: 'var(--spacing-3)' }}>Orderinformation</PanelTitle>
              <CreditSummary>
                <SummaryCard>
                  <span className="label">Kund</span>
                  <span className="value">
                    {selectedCreditOrder.first_name} {selectedCreditOrder.last_name}<br />
                    {selectedCreditOrder.email}
                  </span>
                </SummaryCard>
                <SummaryCard>
                  <span className="label">Orderdatum</span>
                  <span className="value">{formatDate(selectedCreditOrder.created_at)}</span>
                </SummaryCard>
                <SummaryCard>
                  <span className="label">Betalstatus</span>
                  <span className="value">
                    <Badge tone="info">{selectedCreditOrder.payment_status}</Badge>
                  </span>
                </SummaryCard>
                <SummaryCard>
                  <span className="label">Orderbelopp</span>
                  <span className="value">{formatCurrency(selectedCreditOrder.total_amount)}</span>
                </SummaryCard>
              </CreditSummary>
            </div>
          )}

          <ButtonRow>
            <Button onClick={handleGenerateCreditReceipt} disabled={creditGenerating}>
              {creditGenerating ? 'Genererar…' : '📄 Skapa kreditnota'}
            </Button>
            <Button variant="secondary" onClick={handleClearCreditForm}>Rensa formulär</Button>
          </ButtonRow>
        </Panel>

        <Panel>
          <PanelTitle>Historik för genererade kreditnotor</PanelTitle>
          {creditHistory.length === 0 && (
            <EmptyState>Inga kreditnotor har genererats ännu.</EmptyState>
          )}
          {creditHistory.length > 0 && (
            <HistoryTable>
              <thead>
                <tr>
                  <th>Kreditnummer</th>
                  <th>Order</th>
                  <th>Datum</th>
                  <th>Belopp</th>
                  <th>Orsak</th>
                  <th>Anteckningar</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {creditHistory.map(record => {
                  const displayDate = record.issuedAt || record.createdAt;
                  return (
                    <tr key={record.id}>
                      <td>{record.creditNumber}</td>
                      <td>{record.orderNumber}</td>
                      <td>{displayDate ? formatDate(displayDate) : '—'}</td>
                      <td>{formatCurrency(record.amount)}</td>
                      <td>{record.reason}</td>
                      <td>{record.notes || '—'}</td>
                      <td>
                        <Button variant="secondary" onClick={() => handleRemoveCreditHistory(record.id)}>
                          Ta bort
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </HistoryTable>
          )}
        </Panel>
      </Content>
      )}
    </AccountingWrapper>
  );
};

export default AdminAccounting;
